// src/components/ClothNumberPopup.js
import React, { useState, useEffect, useRef } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import './ClothNumberPopup.css';

const ClothNumberPopup = ({
isOpen,
onClose,
clothNumber,
clubStudio = '',
coupleNames = '',
eventName
}) => {
const { selectedCompetition } = useCompetition();
const [qrCodeUrl, setQrCodeUrl] = useState('');
const canvasRef = useRef(null);
const popupContentRef = useRef(null);

// Use competition data if available, otherwise fallback to props
const competitionName = selectedCompetition?.name || "Competition";
const competitionThumbnail = selectedCompetition?.thumbnail || null;
const specificEventName = eventName || "Event";

// Generate QR code when component opens
useEffect(() => {
if (isOpen && clothNumber) {
generateQRCode();
}
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [isOpen, clothNumber]);

const generateQRCode = () => {
// Don't generate QR code if clothNumber is not available
if (!clothNumber) {
console.warn('No cloth number provided for QR code generation');
return;
}

// Check if QRCode library is already loaded
if (window.QRCode) {
generateQRCodeWithLibrary();
return;
}

// Load QR code library only if not already loaded
const script = document.createElement('script');
script.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
script.onload = () => {
generateQRCodeWithLibrary();
};
script.onerror = () => {
console.error('Failed to load QR code library');
};
document.head.appendChild(script);
};

const generateQRCodeWithLibrary = () => {
try {
// Create a temporary div for QR code generation
const tempDiv = document.createElement('div');
tempDiv.style.position = 'absolute';
tempDiv.style.left = '-9999px';
document.body.appendChild(tempDiv);

// eslint-disable-next-line no-unused-vars
const qr = new window.QRCode(tempDiv, {
text: `https://ultimate.flokraft.dance/running-list/${selectedCompetition?.id || 'default'}/${clothNumber}`,
width: 150,
height: 150,
colorDark: '#000000',
colorLight: '#ffffff',
correctLevel: window.QRCode.CorrectLevel.M
});

// Get the QR code image after a short delay
setTimeout(() => {
const qrImage = tempDiv.querySelector('img');
if (qrImage) {
setQrCodeUrl(qrImage.src);
}
document.body.removeChild(tempDiv);
}, 100);
} catch (error) {
console.error('Error generating QR code:', error);
}
};


const handleDownloadPNG = () => {
if (!canvasRef.current) return;

const canvas = canvasRef.current;
const link = document.createElement('a');
link.download = `cloth-number-${clothNumber || 'unknown'}.png`;
link.href = canvas.toDataURL('image/png');
link.click();
};

const handlePrint = () => {
if (!canvasRef.current) return;

const canvas = canvasRef.current;
const dataURL = canvas.toDataURL('image/png');

// Create a new window for printing
const printWindow = window.open('', '_blank');

if (!printWindow) {
alert('Please allow popups for this website to enable printing.');
return;
}

// Create HTML content for printing
const printHTML = `
<!DOCTYPE html>
<html>
<head>
<title>Cloth Number ${clothNumber}</title>
<style>
@page {
size: A5 landscape;
margin: 0;
}

* {
margin: 0;
padding: 0;
box-sizing: border-box;
}

body {
font-family: Arial, sans-serif;
display: flex;
justify-content: center;
align-items: center;
min-height: 100vh;
background: white;
}

.print-container {
text-align: center;
}

.cloth-image {
max-width: 100%;
max-height: 100vh;
}

@media print {
body {
margin: 0;
padding: 0;
}
.print-container {
page-break-after: always;
}
}
</style>
</head>
<body>
<div class="print-container">
<img src="${dataURL}" alt="Cloth Number ${clothNumber}" class="cloth-image" />
</div>
</body>
</html>
`;

printWindow.document.write(printHTML);
printWindow.document.close();

// Wait for the image to load, then print
printWindow.onload = () => {
setTimeout(() => {
printWindow.print();
printWindow.onafterprint = () => {
printWindow.close();
};
// Fallback: close after 2 seconds if onafterprint doesn't work
setTimeout(() => {
if (!printWindow.closed) {
printWindow.close();
}
}, 2000);
}, 500);
};
};

const setupCanvas = () => {
if (!canvasRef.current) return;

const canvas = canvasRef.current;
const ctx = canvas.getContext('2d');

// Set canvas size (Landscape A5: 210mm x 148mm at 300 DPI for high quality)
canvas.width = 2480; // 210mm at 300 DPI
canvas.height = 1748; // 148mm at 300 DPI

// White background
ctx.fillStyle = '#FFFFFF';
ctx.fillRect(0, 0, canvas.width, canvas.height);

// Wait for fonts to load before drawing content
document.fonts.ready.then(() => {
setTimeout(() => {
drawClothDesign(ctx, canvas);
}, 100);
}).catch(() => {
// Fallback if fonts.ready fails
setTimeout(() => {
drawClothDesign(ctx, canvas);
}, 100);
});
};


const drawClothDesign = (ctx, canvas) => {
const centerX = canvas.width / 2;

// Define content area with padding for the border
const contentPadding = 30;
const contentX = contentPadding;
const contentY = contentPadding;
const contentWidth = canvas.width - (contentPadding * 2);
const contentHeight = canvas.height - (contentPadding * 2);
const borderWidth = 30; // Border thickness

// Draw layout for proper vertical stacking (matching screen layout)
const logoImg = new Image();

// Set crossOrigin for external images
logoImg.crossOrigin = 'anonymous';

logoImg.onload = () => {
// Calculate positions for vertical layout within content area
const logoSize = 180; // Square logo size
const logoX = centerX - logoSize / 2;
const logoY = contentY + 50;

// Draw logo at top-center (square)
ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);

// Event title - below logo
ctx.fillStyle = '#000000';
ctx.textAlign = 'center';
ctx.font = 'bold 60px "C059", "Oswald", "Arial Black", sans-serif';
ctx.fillText(competitionName, centerX, logoY + logoSize + 80);

// Large cloth number - moved down
ctx.font = 'bold 1200px "C059", "Oswald", "Arial Black", sans-serif';
const clothText = clothNumber || '?';
const clothNumberY = logoY + logoSize + 80 + 900; // Moved down by 100px (was 500)
ctx.fillText(clothText, centerX, clothNumberY);

// QR code - moved up
if (qrCodeUrl) {
const img = new Image();
img.onload = () => {
// QR code centered below cloth number (175px x 175px) - moved up
const qrCodeY = clothNumberY +80; // Reduced from 400 to 250
ctx.drawImage(img, centerX - 87.5, qrCodeY, 175, 175);

// FLOKRAFT text below QR code
ctx.textAlign = 'center';
ctx.font = '70px "Abduction", "Bebas Neue", "Impact", sans-serif';
ctx.fillText('FLOKRAFT', centerX, qrCodeY + 250);

// Event name(s) under FLOKRAFT - handle multiple events with word wrap
ctx.font = '30px "Carlito", "Arial", sans-serif';
ctx.fillStyle = '#333333'; // Slightly lighter color for secondary text

// Handle multiple events with word wrapping
const eventNames = specificEventName || 'Event';
const maxEventWidth = 2000;
const eventWords = eventNames.split(', ');
let eventLine = '';
let eventY = qrCodeY + 290;
const eventLineHeight = 35;

for (let n = 0; n < eventWords.length; n++) {
  const testLine = eventLine + (n > 0 ? ', ' : '') + eventWords[n];
  const metrics = ctx.measureText(testLine);
  const testWidth = metrics.width;

  if (testWidth > maxEventWidth && n > 0) {
    ctx.fillText(eventLine, centerX, eventY);
    eventLine = eventWords[n];
    eventY += eventLineHeight;
  } else {
    eventLine = testLine;
  }
}
ctx.fillText(eventLine, centerX, eventY);

// Reset to black for couple info
ctx.fillStyle = '#000000';

// Club/Dancer info at bottom
ctx.font = '40px "Carlito", "Arial", sans-serif';
const coupleText = `${clubStudio || 'Club'} - ${coupleNames || 'Dancers'}`;

// Word wrap for long couple info
const maxWidth = 2000;
const words = coupleText.split(' ');
let line = '';
let y = qrCodeY + 360;
const lineHeight = 50;

for (let n = 0; n < words.length; n++) {
const testLine = line + words[n] + ' ';
const metrics = ctx.measureText(testLine);
const testWidth = metrics.width;
if (testWidth > maxWidth && n > 0) {
ctx.fillText(line, centerX, y);
line = words[n] + ' ';
y += lineHeight;
} else {
line = testLine;
}
}
ctx.fillText(line, centerX, y);

// Draw the main border around all content after everything is rendered
ctx.strokeStyle = '#000000';
ctx.lineWidth = borderWidth;
ctx.strokeRect(contentX, contentY, contentWidth, contentHeight);
};
img.src = qrCodeUrl;
} else {
// Draw the main border around all content even if QR code is not yet loaded
ctx.strokeStyle = '#000000';
ctx.lineWidth = borderWidth;
ctx.strokeRect(contentX, contentY, contentWidth, contentHeight);
}
};

logoImg.onerror = () => {
// If logo fails to load, draw without it
console.warn('Logo failed to load for canvas rendering');

// Event title - top (no logo)
ctx.fillStyle = '#000000';
ctx.textAlign = 'center';
ctx.font = 'bold 60px "C059", "Oswald", "Arial Black", sans-serif';
ctx.fillText(specificEventName, centerX, contentY + 80);

// Large cloth number - moved down
ctx.font = 'bold 900px "C059", "Oswald", "Arial Black", sans-serif';
const clothText = clothNumber || '?';
const clothNumberY = contentY + 680; // Moved down by 100px (was 580)
ctx.fillText(clothText, centerX, clothNumberY);

// QR code - moved up
if (qrCodeUrl) {
const img = new Image();
img.onload = () => {
// QR code centered below cloth number (175px x 175px) - moved up
const qrCodeY = clothNumberY + 80; // Reduced from 400 to 250
ctx.drawImage(img, centerX - 87.5, qrCodeY, 175, 175);

// FLOKRAFT text below QR code
ctx.textAlign = 'center';
ctx.font = '100px "Abduction", "Bebas Neue", "Impact", sans-serif';
ctx.fillText('FLOKRAFT', centerX, qrCodeY + 220);

// Event name(s) under FLOKRAFT - handle multiple events with word wrap
ctx.font = '30px "Carlito", "Arial", sans-serif';
ctx.fillStyle = '#333333'; // Slightly lighter color for secondary text

// Handle multiple events with word wrapping
const eventNames = specificEventName || 'Event';
const maxEventWidth = 2000;
const eventWords = eventNames.split(', ');
let eventLine = '';
let eventY = qrCodeY + 260;
const eventLineHeight = 35;

for (let n = 0; n < eventWords.length; n++) {
  const testLine = eventLine + (n > 0 ? ', ' : '') + eventWords[n];
  const metrics = ctx.measureText(testLine);
  const testWidth = metrics.width;

  if (testWidth > maxEventWidth && n > 0) {
    ctx.fillText(eventLine, centerX, eventY);
    eventLine = eventWords[n];
    eventY += eventLineHeight;
  } else {
    eventLine = testLine;
  }
}
ctx.fillText(eventLine, centerX, eventY);

// Reset to black for couple info
ctx.fillStyle = '#000000';

// Club/Dancer info at bottom
ctx.font = '40px "Carlito", "Arial", sans-serif';
const coupleText = `${clubStudio || 'Club'} - ${coupleNames || 'Dancers'}`;

// Word wrap for long couple info
const maxWidth = 2000;
const words = coupleText.split(' ');
let line = '';
let y = qrCodeY + 300;
const lineHeight = 50;

for (let n = 0; n < words.length; n++) {
const testLine = line + words[n] + ' ';
const metrics = ctx.measureText(testLine);
const testWidth = metrics.width;
if (testWidth > maxWidth && n > 0) {
ctx.fillText(line, centerX, y);
line = words[n] + ' ';
y += lineHeight;
} else {
line = testLine;
}
}
ctx.fillText(line, centerX, y);

// Draw the main border around all content after everything is rendered
ctx.strokeStyle = '#000000';
ctx.lineWidth = borderWidth;
ctx.strokeRect(contentX, contentY, contentWidth, contentHeight);
};
img.src = qrCodeUrl;
} else {
// Draw the main border around all content even if QR code is not yet loaded
ctx.strokeStyle = '#000000';
ctx.lineWidth = borderWidth;
ctx.strokeRect(contentX, contentY, contentWidth, contentHeight);
}
};

// Use competition thumbnail if available, otherwise fallback to default logo
if (competitionThumbnail) {
logoImg.src = competitionThumbnail;
} else {
logoImg.src = '/logo.png';
}
};

useEffect(() => {
if (qrCodeUrl && canvasRef.current) {
setupCanvas();
}
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [qrCodeUrl]);

if (!isOpen) return null;

return (
<div className="cloth-number-popup-overlay" onClick={onClose}>
<div className="cloth-number-popup" onClick={(e) => e.stopPropagation()}>
<button className="close-button" onClick={onClose}>×</button>

<div className="cloth-design" ref={popupContentRef}>
{/* Logo - Top center */}
<div className="logo-section">
{competitionThumbnail ? (
<img
src={competitionThumbnail}
alt={`${competitionName} Logo`}
className="event-logo"
onError={(e) => {
e.target.style.display = 'none';
console.warn('Competition thumbnail not found, fallback to default logo');
e.target.src = '/logo.png';
}}
/>
) : (
<img
src="/logo.png"
alt="LOF Logo"
className="event-logo"
onError={(e) => {
e.target.style.display = 'none';
console.warn('Logo not found at /logo.png');
}}
/>
)}
</div>

{/* Event Title - Below logo */}
<div className="header">
<div className="header-title">{competitionName}</div>
</div>

{/* Large cloth number - Biggest text */}
<div className="cloth-number-section">
<div className="cloth-number">{clothNumber}</div>
</div>

{/* QR Code section - Below cloth number */}
<div className="qr-section">
{qrCodeUrl ? (
<img
src={qrCodeUrl}
alt={`QR Code for ${clothNumber}`}
className="qr-code"
/>
) : (
<div className="qr-code-loading">Generating QR Code...</div>
)}
<div className="flokraft">FLOKRAFT</div>
<div className="couple-info">{clubStudio} - {coupleNames}</div>
</div>
</div>

{/* Hidden canvas for download - Landscape A5 size at 300 DPI */}
<canvas
ref={canvasRef}
style={{ display: 'none' }}
width={2480}
height={1748}
/>

{/* Action buttons */}
<div className="popup-actions">
<button className="action-button print-button" onClick={handlePrint}>
🖨️ Print
</button>
<button className="action-button download-button" onClick={handleDownloadPNG}>
📥 Download PNG
</button>
</div>
</div>
</div>
);
};

export default ClothNumberPopup;